/**
 * @jsxRuntime classic
 * @jsx jsx
 * @jsxFrag React.Fragment
 */
import React, {
	type ComponentProps,
	type ComponentType,
	useCallback,
	useEffect,
	useRef,
	useState,
} from 'react';

import { css, jsx } from '@compiled/react';
import debounce from 'lodash/debounce';
import { useIntl } from 'react-intl-next';

import { useAnalyticsEvents } from '@atlaskit/analytics-next';
import { cssMap } from '@atlaskit/css';
import FeatureGates from '@atlaskit/feature-gate-js-client';
import { fg } from '@atlaskit/platform-feature-flags';
import { Box, Flex, Inline, Stack, Text } from '@atlaskit/primitives/compiled';
import type {
	GroupHeadingProps,
	GroupProps,
	SingleValue,
	SingleValueProps,
} from '@atlaskit/react-select';
import Select, {
	components,
	type GroupType,
	type OptionProps,
	type OptionType,
} from '@atlaskit/select';
import Spinner from '@atlaskit/spinner';
import { token } from '@atlaskit/tokens';
import { fireUIAnalytics } from '@atlassian/analytics-bridge';
import {
	Action,
	type ActionSubject,
	getUIEventName,
	UIActionSubject,
} from '@atlassian/atlassian-studio-observability';

import messages from './messages';
import useAsync from './use-async';

type ProjectType = 'software' | 'service_desk' | 'business' | 'product_discovery';

type ProjectStyle = 'COMPANY_MANAGED_PROJECT' | 'TEAM_MANAGED_PROJECT';

type PickerCache = Record<string, GroupedPickerOptions>;

export interface GroupedPickerOptions {
	all: PickerOption[];
	queryEndCursor?: string;
	starred: PickerOption[];
}

export type PickerOption = OptionType & {
	displayName: string;
	iconUrl: string;
	id: string;
	permittedToAdminister: boolean;
	projectAri: string;
	projectId?: number;
	projectStyle?: ProjectStyle;
	projectType?: ProjectType;
	url?: string;
};

interface PickerProps {
	actionSubjectId?: ActionSubject;
	cloudId: string;
	defaultValue?: string;
	idKey: string;
	isOpen?: boolean;
	menuPosition?: ComponentProps<typeof Select>['menuPosition'];
	onChange: (newValue: SingleValue<PickerOption>) => React.ReactNode | undefined;
	onError: (e: Error) => React.ReactNode;
	placeholder?: string;
	postChange?: (value: SingleValue<PickerOption>) => Promise<void>;
	resolver: (
		cloudId: string,
		inputValue: string,
		endCursor?: string,
	) => Promise<GroupedPickerOptions>;
}

const styles = cssMap({
	optionTextWrapper: {
		paddingLeft: token('space.025'),
	},
	customAvatarContainer: {
		display: 'inline-block',
		position: 'relative',
		outline: '0',
	},
	customAvatarImg: {
		display: 'flex',
		width: '100%',
		height: '100%',
		flex: '1 1 100%',
	},
	selectMenu: {
		// This is fixing the zIndex issue when using toggle with select
		// https://atlassian.slack.com/archives/CFJ9DU39U/p1749019887438689
		zIndex: 100,
	},
});

const customAvatarInnerContainer = css({
	display: 'flex',
	boxSizing: 'content-box',
	position: 'static',
	boxAlign: 'stretch',
	alignItems: 'stretch',
	boxPack: 'center',
	justifyContent: 'center',
	flexDirection: 'column',
	border: 'none',
	cursor: 'inherit',
	marginBlockEnd: token('space.025'),
	marginBlockStart: token('space.025'),
	marginInlineEnd: token('space.025'),
	marginInlineStart: token('space.025'),
	outline: 'none',
	overflow: 'hidden',
	transition: 'transform 200ms, opacity 200ms',
	borderRadius: token('radius.small'),
	width: '20px',
	height: '20px',
});

const CustomAvatar = ({ src }: { src: string }) => (
	<Box as="div" xcss={styles.customAvatarContainer}>
		<div css={customAvatarInnerContainer}>
			<Box as="img" src={src} xcss={styles.customAvatarImg} alt="" />
		</div>
	</Box>
);

const CustomOption = (props: OptionProps<PickerOption>) => (
	<components.Option {...props}>
		<Inline alignBlock="center" space="space.025">
			<CustomAvatar src={props.data.iconUrl} />
			<Box xcss={styles.optionTextWrapper}>
				<Text>{props.data.displayName}</Text>
			</Box>
		</Inline>
	</components.Option>
);

const CustomGroupHeading = (props: GroupHeadingProps<PickerOption>) => (
	<components.GroupHeading {...props}>
		<Text color="color.text.subtle" weight="bold" size="small">
			{props.data.label}
		</Text>
	</components.GroupHeading>
);

const CustomSingleValue = (props: SingleValueProps<PickerOption>) => (
	<components.SingleValue {...props}>
		<Inline alignBlock="center" space="space.025">
			<CustomAvatar src={props.data.iconUrl} />
			<Box xcss={styles.optionTextWrapper}>
				<Text>{props.data.displayName}</Text>
			</Box>
		</Inline>
	</components.SingleValue>
);

const Picker = ({
	resolver,
	onChange,
	onError,
	cloudId,
	placeholder,
	idKey,
	postChange,
	actionSubjectId,
	menuPosition,
}: PickerProps) => {
	const { formatMessage } = useIntl();
	const [errorText, setErrorText] = useState<React.ReactNode | undefined>();
	const [endCursor, setEndCursor] = useState<string | undefined>(undefined);
	const [searchValue, setSearchValue] = useState<string>('');
	const [allOptions, setAllOptions] = useState<GroupType<PickerOption>[]>([]);
	const cache = useRef<PickerCache>({});
	const { createAnalyticsEvent } = useAnalyticsEvents();

	const loadOptions = useCallback(
		async (queryCursor?: string): Promise<void> => {
			// Don't want to mix up user search and pagination as they both use this callback
			if (actionSubjectId && queryCursor === undefined && searchValue) {
				fireUIAnalytics(
					createAnalyticsEvent({}),
					getUIEventName(UIActionSubject.PICKER, Action.SEARCH),
					actionSubjectId,
					{ searchTerm: searchValue },
				);
			}
			if (actionSubjectId && queryCursor) {
				fireUIAnalytics(
					createAnalyticsEvent({}),
					getUIEventName(UIActionSubject.PICKER, Action.PAGINATE),
					actionSubjectId,
				);
			}
			try {
				let data: GroupedPickerOptions = cache.current[searchValue];
				if (data && queryCursor !== data.queryEndCursor) {
					resolver(cloudId, searchValue, queryCursor)
						.then((newData) => {
							cache.current[searchValue] = newData;
						})
						.catch((err) => {
							throw err;
						});
				} else {
					data = await resolver(cloudId, searchValue, queryCursor);
					cache.current[searchValue] = data;
				}

				const { starred, all, queryEndCursor } = data;
				setEndCursor(queryEndCursor);
				setAllOptions((prev) => {
					if (queryCursor) {
						return [
							{
								label: formatMessage(messages.starred),
								options: starred,
							},
							{
								label: formatMessage(messages.all),
								options: [...prev[1].options, ...all],
							},
						];
					}
					return [
						{
							label: formatMessage(messages.starred),
							options: starred,
						},
						{
							label: formatMessage(messages.all),
							options: all,
						},
					];
				});
			} catch (e: any) {
				setErrorText(onError(e));
				setEndCursor(undefined);
			}
		},
		[actionSubjectId, createAnalyticsEvent, searchValue, resolver, cloudId, formatMessage, onError],
	);
	const optionsResponse = useAsync(loadOptions);
	const [loading, setLoading] = useState<boolean>(false);

	const handleOnChange = useCallback(
		(newValue: SingleValue<PickerOption>) => {
			setErrorText(onChange(newValue));
			if (postChange) {
				setLoading(true);
				postChange(newValue)
					.then(() => setLoading(false))
					.catch((e) => {
						setLoading(false);
						setErrorText(onError(e));
					});
			}
		},
		[onChange, postChange, onError],
	);

	const Group: ComponentType<GroupProps<PickerOption, false, GroupType<PickerOption>>> = (
		props,
	) => {
		const componentRef = useRef(null);

		useEffect(() => {
			const observer = new IntersectionObserver(([entry]) => {
				if (entry.isIntersecting) {
					loadOptions(endCursor);
				}
			});
			if (componentRef.current !== null) {
				// Start observing the element
				observer.observe(componentRef.current);
			}

			return () => observer.disconnect();
		}, [componentRef]);

		return (
			<>
				<components.Group {...props} />
				{props.data.label === formatMessage(messages.all) && endCursor && (
					<Flex justifyContent="center">
						<Spinner ref={componentRef} />
					</Flex>
				)}
			</>
		);
	};

	return (
		<Stack testId={idKey} space="space.050">
			<Select<PickerOption>
				filterOption={() => true}
				isLoading={optionsResponse.state === 'loading'}
				options={optionsResponse.state === 'success' ? allOptions : []}
				placeholder={placeholder}
				components={{
					MenuPortal: (props) => <components.MenuPortal {...props} xcss={styles.selectMenu} />,
					...(fg('studio_rovo_triggers')
						? {
								SingleValue: CustomSingleValue,
								Option: CustomOption,
								GroupHeading: CustomGroupHeading,
								Group,
							}
						: {
								Option: CustomOption,
								GroupHeading: CustomGroupHeading,
								Group,
							}),
				}}
				onChange={handleOnChange}
				onInputChange={debounce((newValue) => setSearchValue(newValue), 200)}
				key={idKey}
				label={formatMessage(
					FeatureGates.getExperimentValue('project-terminology-refresh', 'isEnabled', false)
						? messages.pickerLabelGalaxia
						: messages.pickerLabel,
				)}
				onMenuOpen={() => {
					if (actionSubjectId) {
						fireUIAnalytics(
							createAnalyticsEvent({}),
							getUIEventName(UIActionSubject.PICKER, Action.OPENED),
							actionSubjectId,
						);
					}
				}}
				menuPosition={menuPosition}
			/>
			{loading && <Spinner size="small" />}
			{errorText}
		</Stack>
	);
};

export default Picker;
